import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { LoginPage } from '../features/auth/LoginPage';
import { ManagerAccountDetailPage } from '../features/billing/ManagerAccountDetailPage';
import { ManagerAccountsPage } from '../features/billing/ManagerAccountsPage';
import { MarketingPage } from '../features/marketing/MarketingPage';
import { ExpensesPage } from '../features/expenses/ExpensesPage';
import { GstPage } from '../features/gst/GstPage';
import { MonthEndPage } from '../features/month-end/MonthEndPage';
import { AppPerformancePage } from '../features/reports/AppPerformancePage';
import { MarketingReportPage } from '../features/reports/MarketingReportPage';
import { PnlReportPage } from '../features/reports/PnlReportPage';
import { MorningEntryPage } from '../features/morning-entry/MorningEntryPage';
import { RevenueEntriesPage } from '../features/revenue/RevenueEntriesPage';
import { RevenueReportPage } from '../features/revenue/RevenueReportPage';
import { ProfilePage } from '../features/auth/ProfilePage';
import { CurrencyRatesPage } from '../features/currency/CurrencyRatesPage';
import {
  admobAccountsConfig,
  adxAccountsConfig,
  advertisingAccountsConfig,
  appsConfig,
  firebaseAccountsConfig,
  playDeveloperAccountsConfig,
  gstAccountsConfig,
  revenueAccountsConfig,
} from '../features/masters/configs';
import {
  AdvertisingAccountDetailPage,
  AppDetailPage,
  PlayDeveloperAccountDetailPage,
  RevenueAccountDetailPage,
} from '../features/masters/detailPages';
import { MasterPage } from '../features/masters/MasterPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { AppLayout } from '../layouts/AppLayout';
import { DashboardPage } from '../pages/DashboardPage';
import { NotFoundPage } from '../pages/PlaceholderPages';
import { ProtectedRoute, RequirePermission } from './guards';

/** Old URLs (before the account restructure) keep working. */
function RedirectWithId({ to }: { to: string }) {
  const { id } = useParams();
  return <Navigate to={`${to}/${id}`} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          {
            path: 'morning-entry',
            element: (
              <RequirePermission permission="transactions.write">
                <MorningEntryPage />
              </RequirePermission>
            ),
          },
          { path: 'apps', element: <MasterPage config={appsConfig} /> },
          { path: 'apps/:id', element: <AppDetailPage /> },
          { path: 'marketing', element: <MarketingPage /> },
          { path: 'revenue', element: <RevenueEntriesPage /> },
          { path: 'revenue/entries', element: <Navigate to="/revenue" replace /> },
          // Revenue sources are managed under Settings, like ad platforms.
          {
            path: 'revenue/sources',
            element: <Navigate to="/settings?tab=revenue-sources" replace />,
          },
          { path: 'revenue-accounts', element: <MasterPage config={revenueAccountsConfig} /> },
          { path: 'revenue-accounts/:id', element: <RevenueAccountDetailPage /> },
          { path: 'revenue/accounts', element: <Navigate to="/revenue-accounts" replace /> },
          { path: 'admob-accounts', element: <MasterPage config={admobAccountsConfig} /> },
          { path: 'adx-accounts', element: <MasterPage config={adxAccountsConfig} /> },
          { path: 'month-end', element: <MonthEndPage /> },
          { path: 'expenses', element: <ExpensesPage /> },
          { path: 'gst', element: <GstPage /> },
          { path: 'import', element: <Navigate to="/settings?tab=import" replace /> },
          { path: 'reports/daily', element: <PnlReportPage key="day" granularity="day" /> },
          { path: 'reports/monthly', element: <PnlReportPage key="month" granularity="month" /> },
          { path: 'reports/yearly', element: <PnlReportPage key="year" granularity="year" /> },
          { path: 'reports/apps', element: <AppPerformancePage /> },
          { path: 'reports/marketing', element: <MarketingReportPage /> },
          { path: 'reports/revenue', element: <RevenueReportPage /> },
          { path: 'reports/gst', element: <GstPage key="report" defaultTab="overview" /> },
          { path: 'currency-rates', element: <CurrencyRatesPage /> },
          { path: 'ads-accounts', element: <MasterPage config={advertisingAccountsConfig} /> },
          { path: 'ads-accounts/:id', element: <AdvertisingAccountDetailPage /> },
          { path: 'mcc-accounts', element: <ManagerAccountsPage /> },
          { path: 'mcc-accounts/:id', element: <ManagerAccountDetailPage /> },
          { path: 'advertising-accounts', element: <Navigate to="/ads-accounts" replace /> },
          {
            path: 'advertising-accounts/:id',
            element: <RedirectWithId to="/ads-accounts" />,
          },
          { path: 'manager-accounts', element: <Navigate to="/mcc-accounts" replace /> },
          { path: 'manager-accounts/:id', element: <RedirectWithId to="/mcc-accounts" /> },
          { path: 'gst-accounts', element: <MasterPage config={gstAccountsConfig} /> },
          { path: 'firebase-accounts', element: <MasterPage config={firebaseAccountsConfig} /> },
          {
            path: 'play-developer-accounts',
            element: <MasterPage config={playDeveloperAccountsConfig} />,
          },
          { path: 'play-developer-accounts/:id', element: <PlayDeveloperAccountDetailPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
